import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import { FaCrosshairs, FaMoneyBillWave, FaChartLine, FaBolt, FaShieldAlt, FaCheckCircle, FaHandshake, FaLock } from 'react-icons/fa';
import SEO from '../../components/common/SEO';
import theme from '../../theme';

const CustomPricingSection = styled.section`
  padding: 6rem 0 4rem;
  overflow: hidden;
`;

const HeroSection = styled.div`
  position: relative;
  min-height: 600px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  margin-bottom: 5rem;
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: url('${process.env.PUBLIC_URL}/custom-pricing-1.jpeg');
    background-size: cover;
    background-position: center;
    opacity: 0.1;
    z-index: 0;
  }
  
  &:after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    right: 0;
    height: 100px;
    background: linear-gradient(to bottom, transparent, ${theme.colors.white});
    z-index: 1;
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    min-height: 500px;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    min-height: 400px;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  text-align: center;
  color: ${theme.colors.white};
  max-width: 900px;
  padding: 0 2rem;
`;

const HeroTag = styled.span`
  display: inline-block;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  padding: 0.75rem 1.5rem;
  border-radius: 50px;
  font-size: ${theme.fontSizes.small};
  font-weight: 600;
  margin-bottom: 2rem;
  text-transform: uppercase;
  letter-spacing: 2px;
  border: 1px solid rgba(255, 255, 255, 0.3);
`;

const HeroTitle = styled.h1`
  font-size: 4rem;
  margin-bottom: 1.5rem;
  font-weight: 700;
  line-height: 1.2;
  text-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 3rem;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 2rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: ${theme.fontSizes.xlarge};
  line-height: 1.8;
  margin-bottom: 3rem;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  opacity: 0.95;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: ${theme.fontSizes.large};
  }
`;

const HeroButtons = styled.div`
  display: flex;
  gap: 1.5rem;
  justify-content: center;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    flex-direction: column;
    align-items: center;
  }
`;

const PrimaryButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem 2.5rem;
  background-color: ${theme.colors.white};
  color: #667eea;
  font-size: ${theme.fontSizes.medium};
  font-weight: 700;
  border-radius: 50px;
  transition: all 0.3s ease;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  
  &:hover {
    transform: translateY(-3px) scale(1.05);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.3);
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 100%;
    max-width: 300px;
  }
`;

const SecondaryButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem 2.5rem;
  background-color: transparent;
  color: ${theme.colors.white};
  font-size: ${theme.fontSizes.medium};
  font-weight: 700;
  border-radius: 50px;
  border: 2px solid ${theme.colors.white};
  transition: all 0.3s ease;
  
  &:hover {
    background-color: ${theme.colors.white};
    color: #667eea;
    transform: translateY(-3px);
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 100%;
    max-width: 300px;
  }
`;

const FeaturesSection = styled.div`
  margin-bottom: 5rem;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2.5rem;
  margin-top: 3rem;
  
  @media (max-width: ${theme.breakpoints.desktop}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  background: ${theme.colors.white};
  padding: 2.5rem;
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  position: relative;
  overflow: hidden;
  text-align: center;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
    
    &:before {
      transform: scaleX(1);
    }
  }
`;

const FeatureIcon = styled.div`
  font-size: 3.5rem;
  color: ${theme.colors.primary};
  margin-bottom: 1.5rem;
  line-height: 1;

  svg {
    width: 48px;
    height: 48px;
  }
`;

const FeatureTitle = styled.h3`
  font-size: ${theme.fontSizes.large};
  color: ${theme.colors.secondary};
  margin-bottom: 1rem;
  font-weight: 700;
`;

const FeatureDescription = styled.p`
  color: ${theme.colors.darkGray};
  line-height: 1.7;
  font-size: ${theme.fontSizes.medium};
`;

const ProcessSection = styled.div`
  background: linear-gradient(to bottom, ${theme.colors.lightGray} 0%, ${theme.colors.white} 100%);
  padding: 5rem 0;
  margin: 5rem 0;
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: 2.5rem;
  color: ${theme.colors.secondary};
  margin-bottom: 1rem;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 2rem;
  }
`;

const SectionSubtitle = styled.p`
  text-align: center;
  font-size: ${theme.fontSizes.large};
  color: ${theme.colors.darkGray};
  max-width: 700px;
  margin: 0 auto 4rem;
`;

const ProcessGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
  margin-top: 3rem;
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    top: 50px;
    left: 10%;
    right: 10%;
    height: 2px;
    background: linear-gradient(90deg, 
      transparent 0%, 
      #667eea 10%, 
      #667eea 90%, 
      transparent 100%
    );
    z-index: 0;
  }
  
  @media (max-width: ${theme.breakpoints.desktop}) {
    grid-template-columns: repeat(2, 1fr);
    
    &:before {
      display: none;
    }
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const ProcessStep = styled.div`
  position: relative;
  text-align: center;
  z-index: 1;
`;

const ProcessNumber = styled.div`
  width: 100px;
  height: 100px;
  margin: 0 auto 1.5rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
  font-weight: 700;
  color: ${theme.colors.white};
  box-shadow: 0 10px 30px rgba(102, 126, 234, 0.4);
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    inset: -10px;
    border-radius: 50%;
    border: 2px dashed #667eea;
    opacity: 0.3;
  }
`;

const ProcessTitle = styled.h3`
  font-size: ${theme.fontSizes.large};
  color: ${theme.colors.secondary};
  margin-bottom: 1rem;
  font-weight: 600;
`;

const ProcessDescription = styled.p`
  color: ${theme.colors.darkGray};
  line-height: 1.6;
`;

const BenefitsSection = styled.div`
  margin: 5rem 0;
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

const BenefitsImage = styled.div`
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  
  img {
    width: 100%;
    height: auto;
    display: block;
  }
  
  &:after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(102, 126, 234, 0.3) 0%, rgba(118, 75, 162, 0.3) 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover:after {
    opacity: 1;
  }
`;

const BenefitsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const BenefitItem = styled.div`
  display: flex;
  gap: 1.5rem;
  padding: 1.5rem;
  background: ${theme.colors.white};
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateX(10px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  }
`;

const BenefitIcon = styled.div`
  flex-shrink: 0;
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  color: ${theme.colors.white};
`;

const BenefitContent = styled.div`
  flex: 1;
`;

const BenefitTitle = styled.h4`
  font-size: ${theme.fontSizes.large};
  color: ${theme.colors.secondary};
  margin-bottom: 0.5rem;
  font-weight: 600;
`;

const BenefitText = styled.p`
  color: ${theme.colors.darkGray};
  line-height: 1.6;
`;

const CTASection = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 5rem 2rem;
  border-radius: 30px;
  text-align: center;
  position: relative;
  overflow: hidden;
  margin-top: 5rem;
  
  &:before {
    content: '';
    position: absolute;
    top: -50%;
    right: -10%;
    width: 500px;
    height: 500px;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
    border-radius: 50%;
  }
  
  &:after {
    content: '';
    position: absolute;
    bottom: -50%;
    left: -10%;
    width: 500px;
    height: 500px;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
    border-radius: 50%;
  }
`;

const CTAContent = styled.div`
  position: relative;
  z-index: 1;
  max-width: 700px;
  margin: 0 auto;
  color: ${theme.colors.white};
`;

const CTATitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 1.5rem;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 2rem;
  }
`;

const CTAText = styled.p`
  font-size: ${theme.fontSizes.large};
  margin-bottom: 2.5rem;
  opacity: 0.95;
`;

const CTAButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem 3rem;
  background-color: ${theme.colors.white};
  color: #667eea;
  font-size: ${theme.fontSizes.large};
  font-weight: 700;
  border-radius: 50px;
  transition: all 0.3s ease;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  
  &:hover {
    transform: translateY(-5px) scale(1.05);
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
  }
`;

const CustomPricingPage = () => {
  const { t } = useTranslation();
  const { lang } = useParams();
  
  return (
    <>
      <SEO 
        titleKey="customPricing.seo.title"
        descriptionKey="customPricing.seo.description"
      />
      
      <HeroSection>
        <HeroContent>
          <HeroTag>{t('customPricing.hero.tag')}</HeroTag>
          <HeroTitle>{t('customPricing.hero.title')}</HeroTitle>
          <HeroSubtitle>{t('customPricing.hero.subtitle')}</HeroSubtitle>
          <HeroButtons>
            <PrimaryButton to={`/${lang}/contact`}>
              {t('customPricing.hero.primaryButton')}
            </PrimaryButton>
            <SecondaryButton to={`/${lang}/demo`}>
              {t('customPricing.hero.secondaryButton')}
            </SecondaryButton>
          </HeroButtons>
        </HeroContent>
      </HeroSection>
      
      <CustomPricingSection>
        <div className="container">
          {/* Features */}
          <FeaturesSection>
            <SectionTitle>{t('customPricing.features.title')}</SectionTitle>
            <SectionSubtitle>{t('customPricing.features.subtitle')}</SectionSubtitle>
            
            <FeaturesGrid>
              <FeatureCard>
                <FeatureIcon><FaCrosshairs /></FeatureIcon>
                <FeatureTitle>{t('customPricing.features.items.tailored.title')}</FeatureTitle>
                <FeatureDescription>{t('customPricing.features.items.tailored.description')}</FeatureDescription>
              </FeatureCard>
              
              <FeatureCard>
                <FeatureIcon><FaMoneyBillWave /></FeatureIcon>
                <FeatureTitle>{t('customPricing.features.items.flexible.title')}</FeatureTitle>
                <FeatureDescription>{t('customPricing.features.items.flexible.description')}</FeatureDescription>
              </FeatureCard>
              
              <FeatureCard>
                <FeatureIcon><FaChartLine /></FeatureIcon>
                <FeatureTitle>{t('customPricing.features.items.scalable.title')}</FeatureTitle>
                <FeatureDescription>{t('customPricing.features.items.scalable.description')}</FeatureDescription>
              </FeatureCard>
              
              <FeatureCard>
                <FeatureIcon><FaHandshake /></FeatureIcon>
                <FeatureTitle>{t('customPricing.features.items.support.title')}</FeatureTitle>
                <FeatureDescription>{t('customPricing.features.items.support.description')}</FeatureDescription>
              </FeatureCard>
              
              <FeatureCard>
                <FeatureIcon><FaBolt /></FeatureIcon>
                <FeatureTitle>{t('customPricing.features.items.fast.title')}</FeatureTitle>
                <FeatureDescription>{t('customPricing.features.items.fast.description')}</FeatureDescription>
              </FeatureCard>
              
              <FeatureCard>
                <FeatureIcon><FaLock /></FeatureIcon>
                <FeatureTitle>{t('customPricing.features.items.secure.title')}</FeatureTitle>
                <FeatureDescription>{t('customPricing.features.items.secure.description')}</FeatureDescription>
              </FeatureCard>
            </FeaturesGrid>
          </FeaturesSection>
        </div>
      </CustomPricingSection>
      
      {/* Process */}
      <ProcessSection>
        <div className="container">
          <SectionTitle>{t('customPricing.process.title')}</SectionTitle>
          <SectionSubtitle>{t('customPricing.process.subtitle')}</SectionSubtitle>
          
          <ProcessGrid>
            <ProcessStep>
              <ProcessNumber>1</ProcessNumber>
              <ProcessTitle>{t('customPricing.process.steps.step1.title')}</ProcessTitle>
              <ProcessDescription>{t('customPricing.process.steps.step1.description')}</ProcessDescription>
            </ProcessStep>
            
            <ProcessStep>
              <ProcessNumber>2</ProcessNumber>
              <ProcessTitle>{t('customPricing.process.steps.step2.title')}</ProcessTitle>
              <ProcessDescription>{t('customPricing.process.steps.step2.description')}</ProcessDescription>
            </ProcessStep>
            
            <ProcessStep>
              <ProcessNumber>3</ProcessNumber>
              <ProcessTitle>{t('customPricing.process.steps.step3.title')}</ProcessTitle>
              <ProcessDescription>{t('customPricing.process.steps.step3.description')}</ProcessDescription>
            </ProcessStep>
            
            <ProcessStep>
              <ProcessNumber>4</ProcessNumber>
              <ProcessTitle>{t('customPricing.process.steps.step4.title')}</ProcessTitle>
              <ProcessDescription>{t('customPricing.process.steps.step4.description')}</ProcessDescription>
            </ProcessStep>
          </ProcessGrid>
        </div>
      </ProcessSection>
      
      {/* Benefits */}
      <CustomPricingSection>
        <div className="container">
          <BenefitsSection>
            <SectionTitle>{t('customPricing.benefits.title')}</SectionTitle>
            <SectionSubtitle>{t('customPricing.benefits.subtitle')}</SectionSubtitle>
            
            <BenefitsGrid>
              <BenefitsImage>
                <img 
                  src={`${process.env.PUBLIC_URL}/custom-pricing-2.jpeg`} 
                  alt={t('customPricing.benefits.title')}
                  loading="lazy"
                />
              </BenefitsImage>
              
              <BenefitsList>
                <BenefitItem>
                  <BenefitIcon><FaCheckCircle /></BenefitIcon>
                  <BenefitContent>
                    <BenefitTitle>{t('customPricing.benefits.items.benefit1.title')}</BenefitTitle>
                    <BenefitText>{t('customPricing.benefits.items.benefit1.description')}</BenefitText>
                  </BenefitContent>
                </BenefitItem>
                
                <BenefitItem>
                  <BenefitIcon><FaCheckCircle /></BenefitIcon>
                  <BenefitContent>
                    <BenefitTitle>{t('customPricing.benefits.items.benefit2.title')}</BenefitTitle>
                    <BenefitText>{t('customPricing.benefits.items.benefit2.description')}</BenefitText>
                  </BenefitContent>
                </BenefitItem>
                
                <BenefitItem>
                  <BenefitIcon><FaCheckCircle /></BenefitIcon>
                  <BenefitContent>
                    <BenefitTitle>{t('customPricing.benefits.items.benefit3.title')}</BenefitTitle>
                    <BenefitText>{t('customPricing.benefits.items.benefit3.description')}</BenefitText>
                  </BenefitContent>
                </BenefitItem>
                
                <BenefitItem>
                  <BenefitIcon><FaCheckCircle /></BenefitIcon>
                  <BenefitContent>
                    <BenefitTitle>{t('customPricing.benefits.items.benefit4.title')}</BenefitTitle>
                    <BenefitText>{t('customPricing.benefits.items.benefit4.description')}</BenefitText>
                  </BenefitContent>
                </BenefitItem>
              </BenefitsList>
            </BenefitsGrid>
          </BenefitsSection>
          
          {/* CTA */}
          <CTASection>
            <CTAContent>
              <CTATitle>{t('customPricing.cta.title')}</CTATitle>
              <CTAText>{t('customPricing.cta.description')}</CTAText>
              <CTAButton to={`/${lang}/contact`}>
                {t('customPricing.cta.button')}
              </CTAButton>
            </CTAContent>
          </CTASection>
        </div>
      </CustomPricingSection>
    </>
  );
};

export default CustomPricingPage;

